// src/context/FalContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useToken } from './TokenContext';
import { yorumSec, yorumDoldur } from '../data/yorumlar';
import { FALCILAR } from '../data/falcilar';
import { showFalAd } from '../utils/nativeBridge';

const FalContext = createContext();

export function FalProvider({ children }) {
  const { user, harcaToken } = useToken();
  const [fallar, setFallar] = useState([]);
  const [falciDurumlar, setFalciDurumlar] = useState({});
  const [loading, setLoading] = useState(false);

  // Kullanıcı oturumu açınca falları yükle
  useEffect(() => {
    if (user) {
      fetchFallar();
      fetchFalciDurumlar();
    } else {
      setFallar([]);
    }
  }, [user]);

  // Falları Supabase'den çek, süresi geçmiş bekleyenleri hazır yap
  async function fetchFallar() {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('fallar')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (!error && data) {
      const simdi = new Date();
      const gecmisBeleyenler = data.filter(f => {
        if (f.durum !== 'bekliyor') return false;
        const hazirZaman = f.hazir_olma_zamani
          ? new Date(f.hazir_olma_zamani)
          : new Date(new Date(f.created_at).getTime() + 120 * 1000);
        return hazirZaman < simdi;
      });

      if (gecmisBeleyenler.length > 0) {
        await Promise.all(
          gecmisBeleyenler.map(f =>
            supabase.from('fallar').update({ durum: 'hazir' }).eq('id', f.id)
          )
        );
        setFallar(data.map(f =>
          gecmisBeleyenler.find(g => g.id === f.id) ? { ...f, durum: 'hazir' } : f
        ));
      } else {
        setFallar(data);
      }
    }
    setLoading(false);
  }

  // Falcı meşguliyet durumlarını çek
  async function fetchFalciDurumlar() {
    const { data, error } = await supabase
      .from('falci_durumlar')
      .select('*');

    if (!error && data) {
      const durumMap = {};
      data.forEach(d => {
        // Meşguliyet süresi bittiyse false say
        const mesgulMu = d.mesgul_mu &&
          d.mesgul_bitis &&
          new Date(d.mesgul_bitis) > new Date();
        durumMap[d.falci_id] = mesgulMu;
      });
      setFalciDurumlar(durumMap);
    }
  }

  // Fal gönder
  async function falGonder({ falciId, falTuru, tokenMiktar, fotograflar, dogumTarihi, dogumSaati, dogumYeri, ekNot, ad, burc, soru }) {
    if (!user) return { success: false, error: 'Giriş yapmalısın' };

    // Native uygulamasında reklam göster
    await new Promise(resolve => showFalAd(resolve));

    // Token harca
    const basarili = await harcaToken(tokenMiktar, 'fal');
    if (!basarili) return { success: false, error: 'Yetersiz token' };

    const falci = FALCILAR.find(f => f.id === falciId);

    // AI'dan yorum al
    let yorumMetni = '';
    try {
      let accessToken = null;
      try {
        const sessionData = await supabase.auth.getSession();
        accessToken = sessionData.data.session?.access_token;
        if (!accessToken) {
          const authData = JSON.parse(localStorage.getItem('remil-auth') || '{}');
          accessToken = authData?.access_token;
        }
      } catch {}
      const { data: aiData, error: aiError } = await supabase.functions.invoke('fal-yorumu', {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        body: {
          falciId,
          falTuru,
          falciPrompt: falci?.prompt || '',
          kullaniciVerileri: {
            ...(ad && { ad }),
            ...(burc && { burc }),
            ...(dogumTarihi && { dogumTarihi }),
            ...(dogumSaati && { dogumSaati }),
            ...(dogumYeri && { dogumYeri }),
            ...(soru && { ilgiAlani: soru }),
          },
          falFotografi: fotograflar && fotograflar.length > 0 && fotograflar[0]?.base64 ? 'data:' + (fotograflar[0].type || 'image/jpeg') + ';base64,' + fotograflar[0].base64 : null,
        },
      });

      if (aiError) throw aiError;
      yorumMetni = aiData?.fal || aiData?.falYorumu || '';
    } catch (err) {
      console.error('AI yorum hatası, statik yoruma geçiliyor:', err);
    }

    // AI başarısız olduysa statik yorumlarla devam et
    if (!yorumMetni) {
      const { data: gorulenler } = await supabase
        .from('kullanici_gorulen_yorumlar')
        .select('yorum_id')
        .eq('user_id', user.id)
        .eq('fal_turu', falTuru)
        .eq('falci_id', falciId);

      const gorulenYorumIds = (gorulenler || []).map(g => g.yorum_id);
      const secilenYorum = yorumSec(falTuru, falciId, gorulenYorumIds);
      if (!secilenYorum) return { success: false, error: 'Bu falcı için yorum bulunamadı' };

      yorumMetni = yorumDoldur(secilenYorum, { ad, burc, soru });

      await supabase
        .from('kullanici_gorulen_yorumlar')
        .insert({
          user_id: user.id,
          yorum_id: secilenYorum.id,
          fal_turu: falTuru,
          falci_id: falciId,
        });
    }

    // Bekleme süresi hesapla
    const mesgulMu = falciDurumlar[falciId] || false;
    const beklemeMs = 10 * 60 * 1000;
    const ekSure = mesgulMu ? 1 * 60 * 1000 : 0;
    const hazirOlmaZamani = new Date(Date.now() + beklemeMs + ekSure);

    // 5. Fal kaydını Supabase'e kaydet
    const { data, error } = await supabase
      .from('fallar')
      .insert({
        user_id: user.id,
        falci_id: falciId,
        fal_turu: falTuru,
        durum: 'bekliyor',
        token_harcanan: tokenMiktar,
        dogum_tarihi: dogumTarihi,
        dogum_saati: dogumSaati,
        dogum_yeri: dogumYeri,
        ek_not: ekNot,
        yorum: yorumMetni,
        foto_urls: [],
        hazir_olma_zamani: hazirOlmaZamani.toISOString()
      })
      .select()
      .single();

    if (error) return { success: false, error: error.message };

    // Falcıyı meşgul et
    await supabase
      .from('falci_durumlar')
      .update({
        mesgul_mu: true,
        mesgul_bitis: new Date(Date.now() + 2 * 60 * 1000).toISOString()
      })
      .eq('falci_id', falciId);

    await fetchFallar();
    await fetchFalciDurumlar();

    return { success: true, falId: data.id, yorum: yorumMetni };
  }

  // Falı okundu yap
  async function falOkundu(falId) {
    await supabase
      .from('fallar')
      .update({ durum: 'okundu', okunma_zamani: new Date().toISOString() })
      .eq('id', falId);

    setFallar(prev =>
      prev.map(f => f.id === falId ? { ...f, durum: 'okundu' } : f)
    );
  }

  const bekleyenFallar = fallar.filter(f => f.durum === 'bekliyor');
  const hazirFallar = fallar.filter(f => f.durum === 'hazir');
  const okunmusFallar = fallar.filter(f => f.durum === 'okundu');

  return (
    <FalContext.Provider value={{
      fallar,
      bekleyenFallar,
      hazirFallar,
      okunmusFallar,
      falciDurumlar,
      loading,
      falGonder,
      falOkundu,
      fetchFallar,
      fetchFalciDurumlar
    }}>
      {children}
    </FalContext.Provider>
  );
}

export function useFal() {
  return useContext(FalContext);
}
